import React, { useEffect, useState } from "react";
import { Formik, ErrorMessage } from "formik";

import Input from "components/common/input";
import Check from "components/common/check";
import AsyncSelect from "components/common/select/AsyncSelect";
import ToggleButton from "components/common/togglebutton";

import {
  expenseCategoryInitialValues,
  expenseCategorySchema,
} from "constants/expenseCategory";
import { initialMetaForTable } from "constants/common";

import { getCounties, getStates } from "containers/policies/api";
import {
  useDebouncedCountiesDataCallback,
  useDebouncedStatesDataCallback,
} from "utils/debouncedHelper";
import { capitalizeName, sortData } from "utils/common";

const calculationOptions = [
  { label: "Add to HDA Balance", value: "Add to HDA Balance" },
  { label: "Deduct from HDA Balance", value: "Deduct from HDA Balance" },
  { label: "No Action", value: "No Action" },
];

const authSections = [
  {
    key: "housing_auth",
    label: "Housing Navigation Auth Required?",
  },
  {
    key: "housing_deposit",
    label: "Housing Deposit Auth Required?",
  },
  {
    key: "unit_required",
    label: "Unit Required for Auth?",
  },
];

const AddExpenseCategoryForm = ({
  handleExpenseSubmission,
  currentExpenseEdit,
  handleClose,
}) => {
  const isEditMode = Boolean(currentExpenseEdit);

  const [states, setStates] = useState([]);

  const [countiesBySection, setCountiesBySection] = useState({
    housing_auth: [],
    housing_deposit: [],
    unit_required: [],
  });

  const debouncedStates = useDebouncedStatesDataCallback();
  const debouncedCounties = useDebouncedCountiesDataCallback();

  useEffect(() => {
    const loadStates = async () => {
      try {
        const res = await getStates({ ...initialMetaForTable });
        if (res?.states) {
          setStates(sortData(res.states));
        }
      } catch (err) {
        console.error("Failed to load states:", err);
      }
    };
    loadStates();
  }, []);

  useEffect(() => {
    if (!isEditMode || !currentExpenseEdit) return;

    const sections = [
      {
        prefix: "housing_auth",
        stateId: currentExpenseEdit.housing_auth_state_id,
      },
      {
        prefix: "housing_deposit",
        stateId: currentExpenseEdit.housing_deposit_state_id,
      },
      {
        prefix: "unit_required",
        stateId: currentExpenseEdit.unit_required_state_id,
      },
    ];

    sections.forEach(({ prefix, stateId }) => {
      if (stateId) {
        fetchCounties(prefix, stateId);
      }
    });
  }, [isEditMode, currentExpenseEdit]);

  const fetchCounties = async (prefix, stateId) => {
    if (!stateId) return;

    try {
      const res = await getCounties({
        ...initialMetaForTable,
        state_id: stateId,
      });

      if (res?.counties) {
        setCountiesBySection((prev) => ({
          ...prev,
          [prefix]: sortData(res.counties),
        }));
      }
    } catch (err) {
      console.error(`Failed to load counties for ${prefix}:`, err);
    }
  };

  const getOptionFromValue = (list, value, fallbackEntity) => {
    if (!value) return null;

    if (value && typeof value === "object" && value.label && value.value) {
      return value;
    }

    const numValue = Number(value);
    const found = list.find((item) => Number(item.id) === numValue);

    if (found) {
      return { label: found.name, value: String(found.id) };
    }
    if (fallbackEntity?.name) {
      return {
        label: fallbackEntity.name,
        value: String(fallbackEntity.id ?? value),
      };
    }

    return { label: String(value), value: String(value) };
  };

  const prepareMultiCountyOptions = (counties = []) =>
    counties.map((c) => ({
      label: capitalizeName(c.name),
      value: String(c.id),
    }));

  const handleSectionStateChange = (prefix, selectedState, setFieldValue) => {
    setFieldValue(`expense_category.${prefix}_state_id`, selectedState);
    setFieldValue(`expense_category.${prefix}_county_ids`, "");

    if (selectedState?.value) {
      fetchCounties(prefix, selectedState.value);
    }
  };

  return (
    <Formik
      initialValues={
        isEditMode && currentExpenseEdit
          ? {
              expense_category: {
                name: currentExpenseEdit.name || "",
                status: currentExpenseEdit.status === "active",

                ...authSections.reduce((acc, { key }) => {
                  const enabled = currentExpenseEdit[key] ?? false;
                  acc[key] = enabled;
                  acc[`${key}_state_id`] = enabled
                    ? getOptionFromValue(
                        states,
                        currentExpenseEdit[`${key}_state_id`],
                        currentExpenseEdit[`${key}_state`],
                      )
                    : null;
                  acc[`${key}_county_ids`] = enabled
                    ? (currentExpenseEdit[`${key}_counties`] || []).map(
                        (c) => ({
                          label: c.name,
                          value: String(c.id),
                        }),
                      )
                    : [];
                  return acc;
                }, {}),

                calculation: currentExpenseEdit.calculation || "",
              },
            }
          : expenseCategoryInitialValues
      }
      validationSchema={expenseCategorySchema}
      enableReinitialize
      onSubmit={async (values) => {
        const ec = values.expense_category;

        const payload = {
          expense_category: {
            ...ec,
            status: ec.status ? "active" : "inactive",
            housing_auth_state_id: ec.housing_auth_state_id?.value
              ? Number(ec.housing_auth_state_id.value)
              : null,
            housing_deposit_state_id: ec.housing_deposit_state_id?.value
              ? Number(ec.housing_deposit_state_id.value)
              : null,
            unit_required_state_id: ec.unit_required_state_id?.value
              ? Number(ec.unit_required_state_id.value)
              : null,

            housing_auth_county_ids: ec.housing_auth_county_ids
              ? ec.housing_auth_county_ids.map((o) => o.value).join(",")
              : "",
            housing_deposit_county_ids: ec.housing_deposit_county_ids
              ? ec.housing_deposit_county_ids.map((o) => o.value).join(",")
              : "",
            unit_required_county_ids: ec.unit_required_county_ids
              ? ec.unit_required_county_ids.map((o) => o.value).join(",")
              : "",
          },
        };

        await handleExpenseSubmission(payload);
      }}
    >
      {({ values, handleChange, setFieldValue, handleSubmit }) => (
        <div className="question-modal">
          <form className="form-main" onSubmit={handleSubmit}>
            <hr className="custom-modal-line mt-0" />

            <ToggleButton
              label="Status"
              name="expense_category.status"
              value={values.expense_category.status ? 1 : 0}
              handleChange={(_, val) =>
                setFieldValue("expense_category.status", !!val)
              }
            />

            <Input
              label="Expense Category Name"
              isRequired
              placeholder="e.g. Security Deposit"
              name="expense_category.name"
              value={values.expense_category.name}
              handleChange={handleChange}
              error={
                <ErrorMessage
                  name="expense_category.name"
                  component="p"
                  className="error-message"
                />
              }
            />

            {authSections.map(({ key, label }) => (
              <div key={key} className="mt-3">
                <Check
                  name={`expense_category.${key}`}
                  checked={values.expense_category[key]}
                  label={label}
                  handleChange={() => {
                    const newVal = !values.expense_category[key];
                    setFieldValue(`expense_category.${key}`, newVal);

                    if (!newVal) {
                      setFieldValue(`expense_category.${key}_state_id`, null);
                      setFieldValue(`expense_category.${key}_county_ids`, []);
                    }
                  }}
                />

                {values.expense_category[key] && (
                  <div className="row mt-2">
                    <div className="col-sm-6">
                      <AsyncSelect
                        label="State"
                        isRequired
                        options={states.map((s) => ({
                          label: s.name.toUpperCase(),
                          value: String(s.id),
                        }))}
                        selectedValue={
                          values.expense_category[`${key}_state_id`]
                        }
                        loadOptions={(input) =>
                          new Promise((res) => debouncedStates(input, res))
                        }
                        placeholder="Select State"
                        handleChange={(option) =>
                          handleSectionStateChange(key, option, setFieldValue)
                        }
                        error={
                          <ErrorMessage
                            name={`expense_category.${key}_state_id`}
                            component="p"
                            className="error-message"
                          />
                        }
                      />
                    </div>

                    <div className="col-sm-6">
                      <AsyncSelect
                        label="County(ies)"
                        isRequired
                        isMulti
                        options={prepareMultiCountyOptions(
                          countiesBySection[key],
                        )}
                        selectedValue={
                          values.expense_category[`${key}_county_ids`]
                        }
                        placeholder="Select County"
                        loadOptions={(input) =>
                          new Promise((res) =>
                            debouncedCounties(
                              input,
                              values.expense_category[`${key}_state_id`]
                                ?.value || "",
                              res,
                            ),
                          )
                        }
                        handleChange={(selected) =>
                          setFieldValue(
                            `expense_category.${key}_county_ids`,
                            selected || [],
                          )
                        }
                        error={
                          <ErrorMessage
                            name={`expense_category.${key}_county_ids`}
                            component="p"
                            className="error-message"
                          />
                        }
                      />
                    </div>
                  </div>
                )}
              </div>
            ))}

            <div className="mt-3">
              <AsyncSelect
                label="Calculation"
                isRequired
                options={calculationOptions}
                selectedValue={
                  values.expense_category.calculation
                    ? {
                        label: values.expense_category.calculation,
                        value: values.expense_category.calculation,
                      }
                    : null
                }
                placeholder="Select Calculation"
                handleChange={(opt) =>
                  setFieldValue(
                    "expense_category.calculation",
                    opt?.value ?? "",
                  )
                }
                error={
                  <ErrorMessage
                    name="expense_category.calculation"
                    component="p"
                    className="error-message"
                  />
                }
              />
            </div>

            <div className="action gap-2 mt-4 d-flex">
              <button
                className="secondary-btn w-50"
                type="button"
                onClick={handleClose}
              >
                Cancel
              </button>
              <button className="primary-btn w-50" type="submit">
                {isEditMode ? "Edit Expense Category" : "Add Expense Category"}
              </button>
            </div>
          </form>
        </div>
      )}
    </Formik>
  );
};

export default AddExpenseCategoryForm;
